import * as THREE from "three";
import { isProductMeshUserData } from "../interfaces/ProductMeshUserData";
import { normalizeGuid } from "../utils/guidUtils";
import SceneObjectData from "../model/sceneObjectData/SceneObjectData";
import { ROOM_LABEL_HOVER_TARGET_KEY } from "../interfaces/IRoomNoteTarget";
import Product from "../model/Product";
import { getSceneObjectStyle, SceneObjectStyleEnum } from "../model/sceneObject/SceneObjectStyle";
import LayerMappingsEnum from "../enums/LayerMappingsEnum";
import { RenderOrderEnum } from "../enums/RenderOrderEnum";

// Hovered: distinct from the selection (orange), hover (dark red), drawable (green) and editing (blue) styles.
// Selected: the same orange as selected scene objects.
const HIGHLIGHT_COLOR = 0x00B8D4;
const SELECTED_COLOR = getSceneObjectStyle(SceneObjectStyleEnum.Selected).color;
// Ghosted objects are 60% transparent: faded enough to set the bundle apart, while the model stays readable
const GHOST_OPACITY_FACTOR = 0.4;
// In 3D the model stays fairly solid while just hovering, and fades further once a bundle is selected
const GHOST_3D_HOVER_OPACITY_FACTOR = 0.6;
const GHOST_3D_SELECTED_OPACITY_FACTOR = 0.3;
// Ghosted objects keep 30% of their colour's saturation, so the coloured bundle stands out more
const GHOST_SATURATION = 0.3;
// Hovered and selected bundles are slightly see-through (20% transparent), still well apart from the ghosted rest.
// In 3D, and for floor coverings, they're as opaque as the original.
const COLORED_OPACITY_FACTOR = 0.8;
const COLORED_OPAQUE_OPACITY_FACTOR = 1;

// The layers of products' 3D geometry. Their ghosts keep writing depth: three.js sorts transparent objects only
// by their centres, so without depth a large floor or wall drawn later would paint over the elements in front
// of it. 2D geometry and annotations keep ghosts that write no depth, which their render order relies on.
const PRODUCT_3D_LAYERS = [
    LayerMappingsEnum.Elements3d, LayerMappingsEnum.Spaces3d, LayerMappingsEnum.Fixtures3d,
    LayerMappingsEnum.FloorCoverings3d, LayerMappingsEnum.WallCoverings3d, LayerMappingsEnum.WallCoverings3dNoTexture,
    LayerMappingsEnum.CeilingCoverings3d, LayerMappingsEnum.CeilingCoverings3dNoTexture, LayerMappingsEnum.Skirtings3d,
];

// Coverings sit millimetres from the wall, floor or ceiling they cover, and their materials are double-sided
// (see TriangleMeshGeometry). Opaque, their front faces hide that; repainted see-through, their back faces and the
// surface behind z-fight, flickering as the camera moves. A small depth bias toward the camera settles it.
const COVERING_3D_LAYERS = [
    LayerMappingsEnum.FloorCoverings3d, LayerMappingsEnum.WallCoverings3d, LayerMappingsEnum.WallCoverings3dNoTexture,
    LayerMappingsEnum.CeilingCoverings3d, LayerMappingsEnum.CeilingCoverings3dNoTexture,
];

// Floor coverings stay fully opaque: they ground the faded model. They aren't ghosted, and in a bundle
// they're coloured without being made see-through.
const FLOOR_COVERING_LAYERS = [LayerMappingsEnum.FloorCoverings2d, LayerMappingsEnum.FloorCoverings3d];

const isOnLayers = (obj: THREE.Object3D, layers: LayerMappingsEnum[]): boolean => layers.some(layer => obj.layers.isEnabled(layer));
const isProduct3d = (obj: THREE.Object3D): boolean => isOnLayers(obj, PRODUCT_3D_LAYERS);
const isCovering3d = (obj: THREE.Object3D): boolean => isOnLayers(obj, COVERING_3D_LAYERS);

// An object's size, for its fixed draw order: the radius of its world bounding sphere
const sizeOf = (obj: THREE.Object3D): number => {
    const geometry = (obj as Partial<THREE.Mesh>).geometry;
    if (!geometry) return 0;
    if (!geometry.boundingSphere) geometry.computeBoundingSphere();
    return (geometry.boundingSphere?.radius ?? 0) * obj.matrixWorld.getMaxScaleOnAxis();
};

// Invisible raycast helpers must stay invisible, see annotationUtils
const HIT_GEOMETRY_NAMES = new Set(["HitBoxMesh", "HitLine"]);

type Renderable = THREE.Object3D & { material: THREE.Material | THREE.Material[] };

const isRenderable = (obj: THREE.Object3D): obj is Renderable =>
    (obj as Partial<Renderable>).material !== undefined;

const isHitGeometry = (obj: Renderable): boolean => {
    if (HIT_GEOMETRY_NAMES.has(obj.name)) return true;
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    return materials.every(m => m.transparent && m.opacity === 0);
};

// Objects that hang under a product's group without being part of the product's geometry:
// annotations (under the apartment, by their parentId) and room name labels (under their room)
const isOverlay = (object: THREE.Object3D): boolean =>
    object.userData instanceof SceneObjectData || !!object.userData?.[ROOM_LABEL_HOVER_TARGET_KEY];

// The product an object belongs to: the nearest ancestor (or itself) carrying product userData.
// Null for overlays, see isOverlay.
export const getProduct = (object: THREE.Object3D | null): Product | null => {
    for (let current = object; current; current = current.parent) {
        if (isOverlay(current)) return null;
        if (isProductMeshUserData(current.userData)) return current.userData.product;
    }
    return null;
};

export const getProductGlobalId = (object: THREE.Object3D | null): string | null => getProduct(object)?.globalId ?? null;

// A plain colour, without the texture that would only tint it. original is the material the clone was made from.
// At a factor of 1 the clone keeps the original's transparency.
const setColor = (clone: THREE.Material, original: THREE.Material, color: number, opacityFactor: number) => {
    const m = clone as THREE.Material & { color?: THREE.Color; map?: THREE.Texture | null };
    m.color?.setHex(color);
    if (m.map !== undefined) m.map = null;
    if (opacityFactor === 1) return;
    m.transparent = true;
    m.opacity = original.opacity * opacityFactor;
};

const setGhosted = (clone: THREE.Material, original: THREE.Material, opacityFactor: number) => {
    clone.transparent = true;
    clone.opacity = original.opacity * opacityFactor;
    desaturate(clone);
};

// Blends the surface colour toward its grey once texture, colour and vertex colours are applied, so textured
// surfaces fade too, which changing the colour alone wouldn't do. Shaders without that step (fat lines) are left as they are.
const desaturate = (material: THREE.Material) => {
    material.onBeforeCompile = shader => {
        shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>
            diffuseColor.rgb = mix(vec3(dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722))), diffuseColor.rgb, ${GHOST_SATURATION.toFixed(3)});`);
    };
    material.customProgramCacheKey = () => `bundleGhost:${GHOST_SATURATION}`;
};

// How each look repaints a clone of the original material
type Look = 'highlight' | 'selected' | 'highlightOpaque' | 'selectedOpaque' | 'ghost' | 'ghost3dHover' | 'ghost3dSelected';
const LOOKS: Record<Look, (clone: THREE.Material, original: THREE.Material) => void> = {
    highlight: (clone, original) => setColor(clone, original, HIGHLIGHT_COLOR, COLORED_OPACITY_FACTOR),
    selected: (clone, original) => setColor(clone, original, SELECTED_COLOR, COLORED_OPACITY_FACTOR),
    highlightOpaque: (clone, original) => setColor(clone, original, HIGHLIGHT_COLOR, COLORED_OPAQUE_OPACITY_FACTOR),
    selectedOpaque: (clone, original) => setColor(clone, original, SELECTED_COLOR, COLORED_OPAQUE_OPACITY_FACTOR),
    ghost: (clone, original) => {
        setGhosted(clone, original, GHOST_OPACITY_FACTOR);
        clone.depthWrite = false;
    },
    // These keep the original's depthWrite, see PRODUCT_3D_LAYERS
    ghost3dHover: (clone, original) => setGhosted(clone, original, GHOST_3D_HOVER_OPACITY_FACTOR),
    ghost3dSelected: (clone, original) => setGhosted(clone, original, GHOST_3D_SELECTED_OPACITY_FACTOR),
};

// See COVERING_3D_LAYERS
const pullTowardCamera = (clone: THREE.Material) => {
    clone.polygonOffset = true;
    clone.polygonOffsetFactor = -1;
    clone.polygonOffsetUnits = -4;
};

/**
 * Colours a selected and a highlighted set of products and ghosts everything else under a root object,
 * by swapping materials on the objects themselves. Materials are shared between products, so they're
 * never mutated: each original gets one clone per look, cached until dispose().
 */
export default class BundleHighlighter {
    #root: THREE.Object3D;
    #originals = new Map<Renderable, THREE.Material | THREE.Material[]>();
    #clones = new Map<string, Map<THREE.Material, THREE.Material>>();  // By look, and whether pulled toward the camera
    #originalRenderOrders = new Map<THREE.Object3D, number>();
    #sizes = new WeakMap<THREE.Object3D, number>();

    constructor(root: THREE.Object3D) {
        this.#root = root;
    }

    // GlobalIds are normalised with normalizeGuid(). A product in both sets shows as selected.
    apply(selectedGlobalIds: Set<string>, highlightedGlobalIds: Set<string>) {
        this.clear();
        const ghosts3d: THREE.Object3D[] = [];
        const colored3d: THREE.Object3D[] = [];
        const ghost3d: Look = selectedGlobalIds.size > 0 ? 'ghost3dSelected' : 'ghost3dHover';

        this.#root.traverse(obj => {
            if (!isRenderable(obj) || isHitGeometry(obj)) return;

            const globalId = normalizeGuid(getProductGlobalId(obj));
            const colored = !!globalId && (selectedGlobalIds.has(globalId) || highlightedGlobalIds.has(globalId));
            const floorCovering = isOnLayers(obj, FLOOR_COVERING_LAYERS);
            if (!colored && floorCovering) return;
            const is3d = isProduct3d(obj);
            const opaque = is3d || floorCovering;
            const look: Look = globalId && selectedGlobalIds.has(globalId) ? (opaque ? 'selectedOpaque' : 'selected')
                : colored ? (opaque ? 'highlightOpaque' : 'highlight')
                : is3d ? ghost3d : 'ghost';
            const covering = isCovering3d(obj);
            const toClone = (material: THREE.Material) => this.#getClone(look, covering, material);

            this.#originals.set(obj, obj.material);
            obj.material = Array.isArray(obj.material) ? obj.material.map(toClone) : toClone(obj.material);
            if (is3d) (colored ? colored3d : ghosts3d).push(obj);
        });

        this.#fixDrawOrder(ghosts3d, colored3d);
    }

    /**
     * Gives every repainted 3D mesh its own draw order. three.js sorts transparent objects by render order and
     * only then by distance, which changes as the camera moves and makes overlapping ghosts flicker.
     * The coloured bundle goes first, so ghosts in front of it blend over it instead of hiding it with their depth.
     * Then the ghosts, the largest (floors, walls) first so smaller ones in front blend over them.
     * All within RenderOrderEnum.Products and the next whole number, so overlays with higher render orders
     * still draw on top.
     */
    #fixDrawOrder(ghosts: THREE.Object3D[], colored: THREE.Object3D[]) {
        const bySizeDescending = (a: THREE.Object3D, b: THREE.Object3D) => this.#sizeOf(b) - this.#sizeOf(a) || a.id - b.id;
        const ordered = [...colored.sort(bySizeDescending), ...ghosts.sort(bySizeDescending)];

        ordered.forEach((obj, i) => {
            this.#originalRenderOrders.set(obj, obj.renderOrder);
            obj.renderOrder = RenderOrderEnum.Products + i / (ordered.length + 1);
        });
    }

    #sizeOf(obj: THREE.Object3D): number {
        let size = this.#sizes.get(obj);
        if (size === undefined) {
            size = sizeOf(obj);
            this.#sizes.set(obj, size);
        }
        return size;
    }

    clear() {
        this.#originals.forEach((material, obj) => obj.material = material);
        this.#originals.clear();
        this.#originalRenderOrders.forEach((renderOrder, obj) => obj.renderOrder = renderOrder);
        this.#originalRenderOrders.clear();
    }

    dispose() {
        this.clear();
        this.#clones.forEach(clones => clones.forEach(m => m.dispose()));
        this.#clones.clear();
    }

    // Covering clones are kept apart, in case a covering shares its material with what it covers
    #getClone(look: Look, covering: boolean, material: THREE.Material): THREE.Material {
        const key = covering ? `${look}:covering` : look;
        let cache = this.#clones.get(key);
        if (!cache) {
            cache = new Map();
            this.#clones.set(key, cache);
        }

        let clone = cache.get(material);
        if (!clone) {
            clone = material.clone();
            LOOKS[look](clone, material);
            if (covering) pullTowardCamera(clone);
            clone.needsUpdate = true;
            cache.set(material, clone);
        }
        return clone;
    }
}
